import { useEffect, useRef } from 'react';

import { Outlet, useNavigate } from '@tanstack/react-router';

import { useAuthStore } from '@/features/auth/auth-store';
import { type DisplayRole } from '@/features/auth/roles';
import { ToastProvider } from '@/features/campaigns/ToastProvider';
import { roleLandingPath } from '@/router/paths';

import { AccessDeniedListener } from './AccessDeniedListener';
import { SideNav } from './SideNav';
import { TopNav } from './TopNav';

/**
 * SCRUM-616 — the authed LAYOUT route component. Replaces the router-less
 * AppShell: instead of an activeId useState + a giant ternary selecting the
 * page, it renders the app chrome (TopNav + SideNav) around a TanStack Router
 * <Outlet/> that renders whichever child route matched the URL.
 *
 * ToastProvider still mounts HERE (SCRUM-322) so toasts fired from any page
 * render regardless of the active route.
 *
 * <main key={role}> is preserved from the old AppShell: remounting the subtree
 * on a role switch guarantees transient page state never leaks across roles
 * (SCRUM-387). Role switching itself now navigates to the role landing via the
 * RoleSwitcher/store; this key is the belt-and-suspenders reset.
 */
export function AppShellLayout() {
  const role = (useAuthStore((s) => s.user?.role) ?? 'Bank Executive') as DisplayRole;
  const navigate = useNavigate();

  // SCRUM-616: on a role SWITCH (demo role switcher), navigate to the new role's
  // landing page — the router equivalent of the old AppShell setActiveId effect
  // (SCRUM-387). Skips the first render (prevRole seeded to the mount role) so a
  // deep-linked URL for the current role is not overridden. Unassigned -> the
  // pending-access route.
  const prevRole = useRef(role);
  useEffect(() => {
    if (prevRole.current === role) return;
    prevRole.current = role;
    const landing = roleLandingPath(role);
    void navigate({ to: landing ?? '/unassigned' });
  }, [role, navigate]);

  return (
    <ToastProvider>
      <AccessDeniedListener />
      <div className="bg-bg-0 text-text-0 flex h-screen flex-col overflow-hidden">
        <TopNav />
        <div className="flex flex-1 overflow-hidden">
          <SideNav />
          <main key={role} className="flex-1 overflow-auto">
            <Outlet />
          </main>
        </div>
      </div>
    </ToastProvider>
  );
}
